import { getChapterTitleFromHref } from './expandableEmbeddedChapter';
import type { ChapterContentResponse, ChapterMetadataResponse } from '../types';

interface ProcessedChapterData {
  title: string;
  content: string;
  isCompleted: boolean;
  contentLoading: boolean;
  previousEmbeddedCompletionById: Record<string, boolean>;
}

interface ProcessedChapterDataParams {
  metadata: ChapterMetadataResponse | null;
  content: ChapterContentResponse;
  href: string;
  linkLabel: string;
  previousEmbeddedRawResults: Array<{ chapterKey: string; completion: boolean }>;
}

/**
 * Processes fetched chapter data into normalized state values.
 */
export const processChapterData = ({
  metadata,
  content,
  href,
  linkLabel,
  previousEmbeddedRawResults,
}: ProcessedChapterDataParams): ProcessedChapterData => {
  const title = metadata?.title ?? getChapterTitleFromHref(href, linkLabel);

  const processedContent = typeof content === 'string' ? content : content.body ?? '';
  const contentLoading = !processedContent;

  const isCompleted = metadata?.userHasCompletedChapter === true;

  /**
   * Build a map of previous chapter completion statuses, keyed by chapter identifier.
   * This enables fast lookup during render to determine accordion initial state
   * (collapsed or expanded)
   */
  const previousEmbeddedCompletionById: Record<string, boolean> = {};

  previousEmbeddedRawResults.forEach(({ chapterKey, completion }) => {
    previousEmbeddedCompletionById[chapterKey] = completion;
  });

  return {
    title,
    content: processedContent,
    isCompleted,
    contentLoading,
    previousEmbeddedCompletionById,
  };
};
